import { Chip } from '@mui/material';
import { TbIcon } from 'components/Figma';
import { ReactNode, useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useUnreadMessageCountFiltered } from 'hooks/state/useReadMessagesStore';
import { useMessages } from 'pages/trading/queryHooks';
import { getChipSxStylingForLevel } from 'styles/utils';

export interface SubNavItem {
  id: string;
  label: string;
  route: string;
}

export interface NavItem {
  icon: React.ReactNode;
  id: string;
  label: string;
  route: string;
  show: boolean;
  badge?: React.ReactNode;
  notificationBadge?: React.ReactNode;
  subItems?: SubNavItem[];
}

export interface NavItemsOptions {
  isSuperAdmin?: boolean;
  isAdminView?: boolean;
  isBeta?: boolean;
  isSupportAdmin?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
function BadgePills({ label, variant = 'Default' }: { label: ReactNode; variant?: 'Default' | 'notification' }) {
  return (
    <Chip
      label={label}
      size="small"
      className={'MuiChip-variantBadgePillDefault'}
      sx={getChipSxStylingForLevel(variant)}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function UnreadMessagesBadge() {
  const { isSuperAdmin } = useContext(TenantConfigContext);
  const { data: messages } = useMessages();
  const unreadCount = useUnreadMessageCountFiltered(messages, isSuperAdmin);

  if (unreadCount === 0) {
    return null;
  }
  return <BadgePills label={<div className={'p-0'}>{unreadCount.toString()}</div>} variant="notification" />;
}

// eslint-disable-next-line react-refresh/only-export-components
function SteeringHubBadges() {
  return <UnreadMessagesBadge />;
}

export const proBadge = <BadgePills label="PRO" />;
export const steeringHubBadges = <SteeringHubBadges />;

export const getNavItems = (_options: NavItemsOptions = {}): NavItem[] => {
  const profitabilitySubItems = [
    { id: 'sidebar.profitability.overview', label: 'Overview', route: '/profitability/overview' },
    {
      id: 'sidebar.profitability.costConfigurations',
      label: 'Cost Configuration',
      route: '/profitability/cost-configurations',
    },
  ];

  return [
    {
      id: 'sidebar.insights.pro',
      label: 'Insights',
      route: '/insights',
      icon: <TbIcon name="Analytics" size="lg" />,
      show: true,
    },
    {
      id: 'sidebar.trading',
      label: 'Trading',
      route: '/steeringhub/insightsfeed',
      icon: <TbIcon name="AIFilled" size="lg" />,
      show: true,
      notificationBadge: steeringHubBadges,
    },
    {
      id: 'sidebar.articleVisibility',
      label: 'Article Visibility',
      route: '/articlevisibility',
      icon: <TbIcon name="ArticleVisibilityFilled" size="lg" />,
      show: true,
    },
    {
      id: 'entities.lens.profitability',
      label: 'Profitability',
      route: '/profitability',
      icon: <TbIcon name="Profitability" size="lg" />,
      show: true,
      subItems: profitabilitySubItems,
    },
    {
      id: 'sidebar.benchmarking',
      label: 'Benchmarking',
      route: '/benchmarking',
      icon: <TbIcon name="Benchmarking" size="lg" />,
      show: true,
      badge: proBadge,
    },
    {
      id: 'sidebar.api',
      label: 'API',
      route: '/api',
      icon: <TbIcon name="API" size="lg" />,
      show: true,
      badge: proBadge,
    },
  ];
};
